// Users › All admins (wave 3, W2_Portal_Tables look): the modern table with
// one person cell (photo, name, phone), Add admin top right, Edit in the
// row, Delete after a question that names the person.
//
// Data, exactly as before: the user list already loaded (fetchUsersOnce on
// arrival) filtered to admins, newest first. Delete calls deleteUser(id) then
// fetchUsersOnce(), only with critical edits on (demo mode says so instead);
// the first admin (admin0001) can never be deleted. The CSV has the same
// columns as before (no print) and needs critical edits on; phone and email
// are hidden in demo mode. Edit / Add open the user form with the page
// number to come back to.
// Call-centre feedback (29 Oct 2026): a copy button beside each number copies
// it without spaces (+251964540448) for MicroSIP; it never opens the row and
// is not there while numbers are hidden.
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { api } from 'common';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import { tokens as c } from '../theme';
import { showToast, confirmDialog } from '../components/feedback';
import { CopyButton, DataTable, PageHeader, calmCaps, formatPhoneDisplay, phoneMatches, toMs } from '../components/modern';

const nameOf = (u) => `${(u && u.firstName) || ''} ${(u && u.lastName) || ''}`.trim();
const initialsOf = (u) =>
  [u && u.firstName, u && u.lastName]
    .map((n) => String(n || '').trim().charAt(0))
    .join('')
    .toUpperCase();

export default function CreateAdmin() {
  const { t, i18n } = useTranslation();
  const settings = useSelector((state) => state.settingsdata.settings);
  const navigate = useNavigate();
  const { deleteUser, fetchUsersOnce } = api;
  const staticusers = useSelector((state) => state.usersdata.staticusers);
  const usersLoading = useSelector((state) => state.usersdata.loading);
  const usersError = useSelector((state) => state.usersdata.error);
  const dispatch = useDispatch();
  const { state } = useLocation();
  const [page, setPage] = useState(Number.isInteger(state?.pageNo) && state.pageNo >= 0 ? state.pageNo : 0);

  useEffect(() => {
    if (Number.isInteger(state?.pageNo) && state.pageNo >= 0) setPage(state.pageNo);
  }, [state]);

  useEffect(() => {
    dispatch(fetchUsersOnce());
  }, [dispatch, fetchUsersOnce]);

  // Admins, newest first (as before).
  const data = useMemo(
    () => (staticusers ? staticusers.filter((user) => user.usertype === 'admin').sort((a, b) => (toMs(b.createdAt) || 0) - (toMs(a.createdAt) || 0)) : []),
    [staticusers]
  );
  const listFailed = !staticusers && !usersLoading && !!(usersError && usersError.flag);
  const allowPrivate = !!settings.AllowCriticalEditsAdmin;
  const L = (key, dflt, opts) => t(key, { defaultValue: dflt, ...(opts || {}) });
  const hiddenText = t('hidden_demo');
  const phoneWord = t('phone', { defaultValue: 'Phone number' });

  const navState = { state: { pageNo: page } };
  const addAdmin = () => navigate('/users/edituser/admin', navState);
  const goEdit = (rowData) => navigate(`/users/edituser/admin/${rowData.id}`, navState);

  const handleDelete = async (rowData) => {
    if (!settings.AllowCriticalEditsAdmin) {
      showToast.warning(t('demo_mode'));
      return;
    }
    if (rowData.id === 'admin0001') {
      showToast.error(t('first_admin_deleted'));
      return;
    }
    const name = nameOf(rowData) || (allowPrivate ? formatPhoneDisplay(rowData.mobile) : '');
    const ok = await confirmDialog({
      title: L('admins_delete_title', 'Delete admin {{name}}?', { name }),
      body: L('admins_delete_body', "This removes the admin's account and their access to the portal. It cannot be undone."),
      confirmLabel: L('delete', 'Delete'),
      cancelLabel: L('confirm_cancel', 'Cancel'),
      destructive: true,
    });
    if (!ok) return;
    dispatch(deleteUser(rowData.id));
    dispatch(fetchUsersOnce());
  };

  const subText = { fontSize: 13, lineHeight: 1.45, color: c.INK_2 };
  const columns = [
    {
      id: 'person',
      title: L('name', 'Name'),
      field: 'firstName',
      sortValue: (row) => nameOf(row),
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 200 }}>
          {row.profile_image ? (
            <Avatar src={row.profile_image} alt="" sx={{ width: 36, height: 36 }} />
          ) : (
            <Avatar aria-hidden="true" sx={{ width: 36, height: 36, bgcolor: c.SURFACE_ALT, color: c.INK_2, fontSize: 14, fontWeight: 600 }}>
              {initialsOf(row) || '?'}
            </Avatar>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.4, color: c.INK, overflowWrap: 'anywhere' }}>{nameOf(row) || L('no_name', 'No name')}</Box>
            <Typography component="div" sx={{ ...subText, display: 'flex', alignItems: 'center', gap: 0.25, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              {allowPrivate ? formatPhoneDisplay(row.mobile) : hiddenText}
              {allowPrivate ? <CopyButton value={row.mobile} phone what={phoneWord} sx={{ my: -0.5 }} /> : null}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: 'email',
      title: t('email'),
      field: 'email',
      sortValue: (row) => (allowPrivate ? row.email || '' : ''),
      render: (row) => (
        <Box component="span" sx={{ overflowWrap: 'anywhere' }}>
          {allowPrivate ? row.email || '' : hiddenText}
        </Box>
      ),
    },
  ];

  // The same CSV columns as before (the photo's address included).
  const exportColumns = [
    { title: t('first_name'), field: 'firstName' },
    { title: t('last_name'), field: 'lastName' },
    { title: t('mobile'), value: (row) => (allowPrivate ? row.mobile || '' : hiddenText) },
    { title: t('email'), value: (row) => (allowPrivate ? row.email || '' : hiddenText) },
    { title: t('profile_image'), field: 'profile_image' },
  ];

  // Name always; phone and email only when they may be shown.
  const searchFn = (row, q, ql) => {
    if (nameOf(row).toLowerCase().includes(ql)) return true;
    if (!allowPrivate) return false;
    if (phoneMatches(row.mobile, q)) return true;
    return String(row.email || '').toLowerCase().includes(ql);
  };

  const actions = [
    { key: 'edit', label: t('edit'), icon: <EditOutlinedIcon />, inline: true, onClick: goEdit },
    { key: 'delete', label: L('admins_delete', 'Delete admin'), icon: <DeleteOutlineRoundedIcon />, destructive: true, onClick: handleDelete },
  ];

  const addLabel = t('add_admin');
  const listTitle = calmCaps(t('alladmins_title'));

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader primaryAction={{ label: addLabel, icon: <PersonAddAltRoundedIcon />, onClick: addAdmin }} />
      <DataTable
        title={listTitle}
        rows={data}
        columns={columns}
        loading={!staticusers && !listFailed}
        error={listFailed ? { title: L('admins_load_failed', 'Could not load admins.'), onRetry: () => dispatch(fetchUsersOnce()) } : null}
        countText={({ shown, total, query, loading }) =>
          loading
            ? L('loading', 'Loading…')
            : query
              ? L('table_count_shown', '{{shown}} of {{total}}', { shown, total })
              : t('admins_count', {
                  count: total,
                  defaultValue_one: '{{count}} admin',
                  defaultValue_other: '{{count}} admins',
                  defaultValue: '{{count}} admins',
                })
        }
        searchFn={searchFn}
        resortKey={allowPrivate ? 'private' : 'demo'}
        searchPlaceholder={allowPrivate ? L('drivers_search_hint', 'Search name, phone or email') : L('customers_search_name', 'Search by name')}
        rowLabel={nameOf}
        actions={actions}
        page={page}
        onPageChange={setPage}
        prefsKey="admins"
        exportCsv={allowPrivate}
        exportColumns={exportColumns}
        exportFileName="admins"
        exportTitle={listTitle}
        emptyState={({ query }) =>
          query
            ? null
            : {
                icon: <AdminPanelSettingsOutlinedIcon />,
                title: L('admins_empty', 'No admins yet'),
                actionLabel: addLabel,
                onAction: addAdmin,
              }
        }
        minWidth={560}
      />
    </Box>
  );
}
